/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useCallback, useState } from "react";
import {
  ConditionalTextField,
  ConditionalTextFieldProps,
} from "./ConditionalTextField";
import { Const } from "const";

interface IntegerTextFieldProps extends ConditionalTextFieldProps {
  minValue?: number;
  maxValue?: number;
}

export const IntegerTextField: FC<IntegerTextFieldProps> = ({
  minValue,
  maxValue,
  ...baseProps
}) => {
  const [localValue, setLocalValue] = useState<number | undefined>(
    baseProps.value !== undefined ? Number(baseProps.value) : undefined
  );
  const [error, setError] = useState(false);

  const validValue = useCallback(
    (value: number) => {
      if (value !== undefined) {
        if (minValue !== undefined && value < minValue) {
          return false;
        }
        if (maxValue !== undefined && value > maxValue) {
          return false;
        }
      }
      return true;
    },
    [maxValue, minValue]
  );

  const handleChanged = useCallback(
    (text: string) => {
      if (text === "") {
        setLocalValue(undefined);
      } else {
        const newValue = Number(text);
        setLocalValue(newValue);
        if (baseProps.textFieldProps?.error !== undefined) {
          setError(baseProps.textFieldProps.error && !validValue(newValue));
        } else {
          setError(!validValue(newValue));
        }
      }
      baseProps.onChanged?.(text);
    },
    [baseProps, validValue]
  );

  return (
    <ConditionalTextField
      {...baseProps}
      value={localValue?.toString()}
      onChanged={handleChanged}
      acceptPattern={Const.regExp.integerPattern}
      textFieldProps={{
        ...baseProps.textFieldProps,
        error: error,
      }}
    />
  );
};
